import { RequestError } from '#errors/index.ts';
import { trimTrailingSlash } from '#utils/index.ts';
import {
  DEFAULT_TIMEOUT,
  throwConnectionError,
  throwResponseError,
  withSessionCookie,
  withTimeout,
} from '#connection/connection.ts';
import { readEvents } from './reader/index.ts';
import type { TSseEvent } from './reader/index.ts';
import {
  buildSseRequest,
  isDrop,
  isEventStream,
  isNoContent,
  resolveReconnect,
} from './sse.helpers.ts';
import type {
  TSseAttempt,
  TSseClient,
  TSseClientOptions,
  TSseStatus,
} from './sse.types.ts';

export const DEFAULT_RECONNECT_DELAY = 3000;

const noAttempt: TSseAttempt = {};

const assertEventStream = async (response: Response): Promise<void> => {
  if (!response.ok) {
    await throwResponseError(response);
  }

  if (isNoContent(response)) {
    return;
  }

  if (!isEventStream(response) || !response.body) {
    throw new RequestError({
      kind: 'decode',
      retryable: false,
      status: response.status,
    });
  }
};

const wait = (delay: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };

    const timer = setTimeout(finish, delay);

    signal.addEventListener('abort', finish, { once: true });

    if (signal.aborted) {
      finish();
    }
  });

export const createSseClient = ({
  url,
  onRequest = withSessionCookie,
  fetch: send = globalThis.fetch,
  connectTimeout = DEFAULT_TIMEOUT,
  reconnect: clientReconnect,
}: TSseClientOptions): TSseClient => {
  const base = trimTrailingSlash(url);

  const open: TSseClient['open'] = async (path, options = {}) => {
    const reconnect = resolveReconnect(clientReconnect, options.reconnect);
    const maxAttempts = reconnect?.maxAttempts ?? Infinity;
    const closer = new AbortController();
    const forwardAbort = () => closer.abort(options.signal?.reason);

    let status: TSseStatus = 'connecting';
    let lastEventId = options.lastEventId;
    let retryDelay = reconnect?.delay ?? DEFAULT_RECONNECT_DELAY;
    let closed = false;

    if (options.signal?.aborted) {
      forwardAbort();
    } else {
      options.signal?.addEventListener('abort', forwardAbort, { once: true });
    }

    const setStatus = (next: TSseStatus) => {
      status = next;
      options.onStatus?.(next);
    };

    const connect = async (): Promise<TSseAttempt> => {
      setStatus('connecting');

      const timer = withTimeout(
        closer.signal,
        options.connectTimeout ?? connectTimeout,
      );

      try {
        const request = buildSseRequest(
          { ...options, lastEventId },
          timer.signal,
        );

        const init = await onRequest(request);

        const response = await send(`${base}${path}`, init);

        timer.clear();
        await assertEventStream(response);

        return { body: response.body ?? undefined, release: timer.release };
      } catch (cause) {
        timer.release();

        return throwConnectionError(cause);
      }
    };

    const reconnectAfter = async (attempts: number): Promise<TSseAttempt> => {
      if (attempts > maxAttempts) {
        throw new RequestError({ kind: 'network', retryable: true });
      }

      setStatus('connecting');
      await wait(retryDelay, closer.signal);

      if (closed) {
        return noAttempt;
      }

      if (closer.signal.aborted) {
        return throwConnectionError(closer.signal.reason);
      }

      return connect();
    };

    const first = await connect();

    const iterate = async function* (): AsyncGenerator<TSseEvent> {
      let attempt = first;
      let attempts = 0;

      try {
        while (attempt.body) {
          setStatus('open');

          try {
            const events = readEvents(attempt.body, {
              signal: closer.signal,
              onRetry: (delay) => {
                retryDelay = delay;
              },
              onClose: attempt.release,
            });

            for await (const event of events) {
              lastEventId = event.id ?? lastEventId;
              yield event;
            }
          } catch (cause) {
            if (closed) {
              return;
            }

            if (!reconnect || !isDrop(cause)) {
              throw cause;
            }
          }

          if (!reconnect || closed) {
            return;
          }

          attempts += 1;
          attempt = await reconnectAfter(attempts).catch((cause: unknown) =>
            closed ? noAttempt : Promise.reject(cause),
          );
        }
      } finally {
        closed = true;
        setStatus('closed');
        options.signal?.removeEventListener('abort', forwardAbort);
      }
    };

    return {
      [Symbol.asyncIterator]: iterate,
      get status() {
        return status;
      },
      get lastEventId() {
        return lastEventId;
      },
      close: () => {
        closed = true;
        closer.abort(new DOMException('closed', 'AbortError'));
      },
    };
  };

  return { open };
};
